/**
 * Core-100 R5 (task I.4) — CORE-U / OD-43: a partially-created shop is not operable.
 *
 * The SERVER is the authority: `public.shop_is_operable` (migration 20270206030900) refuses every ticket in a shop
 * that was founded by the founding act and whose setup was never committed (trigger
 * `trg_ticket_requires_operable_shop`). This gate is the route half of the same rule: a member of such a shop who
 * opens any operational route is sent to the setup wizard instead of a board that cannot take work.
 *
 * It asks the server (`get_shop_operability`), never decides locally. While the answer is pending it renders a
 * loading state, not the operational page. If the read itself fails it renders the page (the server still refuses
 * work), so a transient error cannot lock an operating shop out of its own board.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

export const SHOP_SETUP_PATH = '/onboard/setup';

export type ShopOperability = { operable: boolean; basis: string };

// An operable answer is stable (a committed setup is never un-committed); a non-operable one is re-asked on every
// mount so the guard lets the founder through as soon as the setup commits.
const operableShops = new Set<string>();
export const __resetShopOperabilityCache = () => operableShops.clear();

export async function fetchShopOperability(shopId: string): Promise<ShopOperability | null> {
  try {
    const { data, error } = await supabase.rpc('get_shop_operability' as never, { p_shop_id: shopId } as never);
    if (error || !data) return null;
    const d = data as { operable?: unknown; basis?: unknown };
    if (typeof d.operable !== 'boolean') return null;
    return { operable: d.operable, basis: String(d.basis ?? '') };
  } catch {
    return null;
  }
}

type GateState = 'loading' | 'operable' | 'not_operable' | 'unknown';

export function ShopOperabilityGate({ shopId, children }: { shopId: string | null | undefined; children: ReactNode }) {
  const [state, setState] = useState<GateState>(() => (!shopId || operableShops.has(shopId) ? 'operable' : 'loading'));

  useEffect(() => {
    if (!shopId) { setState('operable'); return; }
    if (operableShops.has(shopId)) { setState('operable'); return; }
    let cancelled = false;
    setState('loading');
    void fetchShopOperability(shopId).then((r) => {
      if (cancelled) return;
      if (r === null) { setState('unknown'); return; }
      if (r.operable) operableShops.add(shopId);
      setState(r.operable ? 'operable' : 'not_operable');
    });
    return () => { cancelled = true; };
  }, [shopId]);

  if (state === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background" data-testid="shop-operability-loading">
        <div className="text-center">
          <div className="text-xl font-semibold">Loading shop...</div>
        </div>
      </div>
    );
  }
  if (state === 'not_operable') return <Navigate to={SHOP_SETUP_PATH} replace />;
  return <>{children}</>;
}

export default ShopOperabilityGate;
